import { apiClient } from '@/api/softphone_api';
import vueApp from '@/store';
import { BASE_PATH, HostError, isValidPbxServer, resolveTarget, unreachableMessage } from '../helpers/host';

// Salida HTTP de Fexería. Un admin SipMóvil habla con el host de la empresa elegida (helpers/host.js) y manda
// ?company_id=; los demás roles usan el host actual. No se usa change_company.

export const BASE = BASE_PATH;
export const url_ = (p) => `${BASE}/${p}`;

// Empresa elegida (admin SipMóvil): { id, pbxServer }, solo en esta pestaña.
const COMPANY_KEY = 'fexeria_company';
let memoryCompany = null;

export const getCompany = () => {
  try {
    const raw = sessionStorage.getItem(COMPANY_KEY);
    return raw ? JSON.parse(raw) : memoryCompany;
  } catch (e) {
    return memoryCompany;
  }
};

// Al cambiar de empresa se cancela lo que está en curso (una subida no debe terminar en otra empresa).
let controller = new AbortController();
export const abortInFlight = () => {
  controller.abort();
  controller = new AbortController();
};

export const setCompany = (company) => {
  const prev = getCompany();
  const next = company && company.id ? { id: company.id, pbxServer: company.pbxServer ?? null } : null;
  memoryCompany = next;
  try {
    if (next) sessionStorage.setItem(COMPANY_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(COMPANY_KEY);
  } catch (e) {
    // sin storage: queda en memoria
  }
  if (prev?.id !== next?.id || prev?.pbxServer !== next?.pbxServer) abortInFlight();
};

// Solo desarrollo: VUE_APP_FEXERIA_HOST_MAP={"pbx2.fexe.co":"https://test.fexe.co"} simula otros hosts.
const devHostMap = () => {
  if (process.env.NODE_ENV === 'production') return {};
  try {
    return JSON.parse(process.env.VUE_APP_FEXERIA_HOST_MAP || '{}') || {};
  } catch (e) {
    return {};
  }
};
const DEBUG = process.env.VUE_APP_FEXERIA_DEBUG === 'true';

export function targetContext(company = getCompany()) {
  const userInfo = vueApp.state.auth_store.userInfo || {};
  return {
    isSuper: (userInfo.groups || []).includes('ADMINISTRADOR SIPMOVIL'),
    company,
    currentHost: userInfo.PbxServer,
    hostMap: devHostMap(),
  };
}

export function currentTarget(company) {
  try {
    const t = resolveTarget(targetContext(company));
    if (t.reason) console.warn('[fexeria]', t.reason);
    return t;
  } catch (e) {
    if (e instanceof HostError) console.error('[fexeria] host de empresa no válido:', e.host);
    throw e;
  }
}

export const assertCompanyHost = (company) => currentTarget(company);

const withCompany = (cfg = {}) => {
  const ctx = targetContext();
  return ctx.isSuper && ctx.company?.id ? { ...cfg, params: { ...cfg.params, company_id: ctx.company.id } } : cfg;
};

// Vía única de salida: si falla el host de la empresa nunca se cae al host actual.
async function request(method, path, data, cfg = {}, retry = true) {
  const target = currentTarget();
  if (DEBUG) console.debug('[fexeria] host destino', target.host, '→', target.origin, method.toUpperCase(), path);
  try {
    return await apiClient.request({
      method,
      url: `${target.origin}/${path}`, // absoluta: axios ignora el baseURL del host actual
      data,
      ...withCompany(cfg),
      signal: controller.signal,
    });
  } catch (e) {
    if (e?.code === 'ERR_CANCELED') throw e;
    if (!e.response) {
      if (!target.remote) throw e;
      const err = new HostError(unreachableMessage(target.host), target.host);
      err.cause = e;
      throw err;
    }
    // 421: la empresa se administra en otro servidor.
    const moved = e.response.status === 421 && e.response.data?.pbx_server;
    if (moved && retry && isValidPbxServer(moved) && getCompany()) {
      setCompany({ ...getCompany(), pbxServer: moved });
      return request(method, path, data, cfg, false);
    }
    throw e;
  }
}

export const http = {
  get: (u, c) => request('get', u, undefined, c),
  delete: (u, c) => request('delete', u, undefined, c),
  post: (u, d, c) => request('post', u, d, c),
  put: (u, d, c) => request('put', u, d, c),
  patch: (u, d, c) => request('patch', u, d, c),
};
